import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faFilterCircleXmark,
  faCalendarDays,
  faEuroSign,
  faChartLineUp,
  faFolderOpen,
  faFilter,
  faUsers,
  faCircleCheck,
  faStar,
  faUserSlash,
  faCircleInfo,
} from '@fortawesome/pro-solid-svg-icons';

import { MetricsParams, MetricsService } from '../../../core/services/metrics.service';
import { Auth } from '../../../core/auth/auth';
import { ContractStatusService } from '../../../core/services/contract-status.service';
import { CustomSelectComponent } from '../../../shared/components/custom-select/custom-select.component';
import { DateRangePicker } from '../../../shared/components/date-range-picker/date-range-picker';
import { PARTNER_TYPE_OPTIONS, PartnerType } from '../../../core/models/partner.model';

const PARTNER_TYPE_COLORS: Record<PartnerType, string> = {
  'Asesoría, Gestoría o Despacho': '#6366f1',
  'Empresa Local de Informática': '#0ea5e9',
  'Canal de Partner Wolters Kluwer': '#14b8a6',
  'Canal de Partner Odoo': '#84cc16',
  'Fabricante de Software': '#f59e0b',
  'Agencia de Marketing o Web': '#f97316',
  'Convenios y Asociaciones': '#ec4899',
  'Consultor o Freelance': '#8b5cf6',
};

@Component({
  selector: 'app-dashboard-page',
  standalone: true,
  imports: [
    CommonModule,
    NgxEchartsDirective,
    FormsModule,
    RouterLink,
    CustomSelectComponent,
    DateRangePicker,
    FontAwesomeModule,
  ],
  providers: [provideEchartsCore({ echarts })],
  templateUrl: './dashboard-page.html',
  styleUrls: ['./dashboard-page.scss'],
})
export class DashboardPage implements OnInit {
  private readonly metricsService = inject(MetricsService);
  private readonly auth = inject(Auth);
  private readonly destroyRef = inject(DestroyRef);
  readonly contractStatusService = inject(ContractStatusService);

  private readonly darkMode = signal(this.isDarkMode());

  readonly isLoading = signal(false);
  readonly errorMessage = signal('');
  readonly metrics = signal<MetricsParams | null>(null);

  // Icons
  readonly faFilterCircleXmark = faFilterCircleXmark;
  readonly faCalendarDays = faCalendarDays;
  readonly faEuroSign = faEuroSign;
  readonly faChartLineUp = faChartLineUp;
  readonly faFolderOpen = faFolderOpen;
  readonly faFilter = faFilter;
  readonly faUsers = faUsers;
  readonly faCircleCheck = faCircleCheck;
  readonly faStar = faStar;
  readonly faUserSlash = faUserSlash;
  readonly faCircleInfo = faCircleInfo;

  // Filters
  readonly startDate = signal<string | null>('2026-01-01');
  readonly endDate = signal<string | null>('2026-12-31');

  // Always return a valid theme string ('dark'|'light') to satisfy ngx-echarts types
  readonly echartsTheme = computed(() => (this.darkMode() ? 'dark' : 'light'));

  readonly partnerTypeOptions = PARTNER_TYPE_OPTIONS;
  readonly partnerTypeColors = PARTNER_TYPE_COLORS;

  readonly showTopPartners = computed(() => {
    return this.isAdmin && (this.metrics()?.top_partners?.length ?? 0) > 0;
  });

  readonly showChannelActivation = computed(() => {
    return this.isAdmin && !!this.metrics()?.channel_activation;
  });

  readonly showTopPartnersBySales = computed(() => {
    return this.isAdmin && (this.metrics()?.top_partners_by_sales?.length ?? 0) > 0;
  });

  readonly showTopPartnersByOpportunities = computed(() => {
    return this.isAdmin && (this.metrics()?.top_partners_by_opportunities?.length ?? 0) > 0;
  });

  readonly donutOptions = computed(() => {
    const metricsData = this.metrics();
    const isDark = this.darkMode();
    const total =
      (metricsData?.by_state?.in_progress ?? 0) +
      (metricsData?.by_state?.won ?? 0) +
      (metricsData?.by_state?.lost ?? 0);

    const getPercent = (val: number) => (total > 0 ? ((val / total) * 100).toFixed(1) : '0.0');

    return {
      backgroundColor: 'transparent',
      color: ['#3b82f6', '#10b981', '#ef4444'],
      tooltip: {
        trigger: 'item',
        backgroundColor: isDark ? '#1f2937' : '#ffffff',
        borderColor: isDark ? '#374151' : '#e5e7eb',
        textStyle: { color: isDark ? '#e5e7eb' : '#374151' },
      },
      legend: {
        orient: 'vertical',
        right: '10%',
        top: 'middle',
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 20,
        textStyle: {
          color: isDark ? '#e5e7eb' : '#374151',
          fontSize: 13,
          rich: {
            name: { width: 80 },
            value: { width: 30, fontWeight: 'bold' },
            percent: { color: '#9ca3af' },
          },
        },
        formatter: (name: string) => {
          let val = 0;
          if (name === 'En curso') val = metricsData?.by_state?.in_progress ?? 0;
          if (name === 'Ganadas') val = metricsData?.by_state?.won ?? 0;
          if (name === 'Perdidas') val = metricsData?.by_state?.lost ?? 0;
          return `{name|${name}} {value|${val}} {percent|(${getPercent(val)}%)}`;
        },
      },
      series: [
        {
          type: 'pie',
          radius: ['70%', '50%'],
          center: ['30%', '50%'],
          avoidLabelOverlap: false,
          label: {
            show: true,
            position: 'center',
            formatter: () => `{total|${total}}\n{label|Total}`,
            rich: {
              total: {
                fontSize: 24,
                fontWeight: 'bold',
                color: isDark ? '#ffffff' : '#111827',
              },
              label: {
                fontSize: 12,
                color: isDark ? '#9ca3af' : '#6b7280',
                padding: [5, 0, 0, 0],
              },
            },
          },
          data: [
            { value: metricsData?.by_state?.in_progress ?? 0, name: 'En curso' },
            { value: metricsData?.by_state?.won ?? 0, name: 'Ganadas' },
            { value: metricsData?.by_state?.lost ?? 0, name: 'Perdidas' },
          ],
        },
      ],
    };
  });

  readonly partnerTypeBarOptions = computed(() => {
    const metricsData = this.metrics();
    const isDark = this.darkMode();
    const byType = metricsData?.by_type;

    // Orden ascendente: echarts pinta las categorías de abajo hacia arriba,
    // así el valor más alto queda arriba.
    const entries = this.partnerTypeOptions
      .map((opt) => ({
        name: opt.label,
        value: byType?.[opt.value] ?? 0,
        color: PARTNER_TYPE_COLORS[opt.value],
      }))
      .sort((a, b) => a.value - b.value);

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: isDark ? '#1f2937' : '#ffffff',
        borderColor: isDark ? '#374151' : '#e5e7eb',
        textStyle: { color: isDark ? '#e5e7eb' : '#374151' },
      },
      grid: { left: 8, right: 32, top: 8, bottom: 8, containLabel: true },
      xAxis: {
        type: 'value',
        axisLabel: { color: isDark ? '#9ca3af' : '#6b7280' },
        splitLine: { lineStyle: { color: isDark ? '#374151' : '#f3f4f6', type: 'dashed' } },
      },
      yAxis: {
        type: 'category',
        data: entries.map((e) => e.name),
        axisLabel: {
          color: isDark ? '#e5e7eb' : '#374151',
          fontSize: 11,
          width: 170,
          overflow: 'truncate',
        },
        axisLine: { lineStyle: { color: isDark ? '#374151' : '#e5e7eb' } },
      },
      series: [
        {
          type: 'bar',
          data: entries.map((e) => ({
            value: e.value,
            itemStyle: { color: e.color, borderRadius: [0, 4, 4, 0] },
          })),
          barMaxWidth: 16,
          label: {
            show: true,
            position: 'right',
            color: isDark ? '#e5e7eb' : '#374151',
            fontSize: 11,
          },
        },
      ],
    };
  });

  readonly lineOptions = computed(() => {
    const isDark = this.darkMode();
    const temporal = this.metrics()?.temporal;
    const labels = temporal?.labels ?? [];
    const created = temporal?.created ?? [];
    const closed = temporal?.closed ?? [];

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor: isDark ? '#1f2937' : '#ffffff',
        borderColor: isDark ? '#374151' : '#e5e7eb',
        textStyle: { color: isDark ? '#e5e7eb' : '#374151' },
      },
      legend: {
        top: 0,
        left: 0,
        textStyle: { color: isDark ? '#9ca3af' : '#4b5563' },
        itemWidth: 12,
        itemHeight: 12,
      },
      grid: { left: 40, right: 24, top: 40, bottom: 24, containLabel: true },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: labels,
        axisLabel: { color: isDark ? '#9ca3af' : '#6b7280' },
        axisLine: { lineStyle: { color: isDark ? '#374151' : '#e5e7eb' } },
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: isDark ? '#9ca3af' : '#6b7280' },
        splitLine: { lineStyle: { color: isDark ? '#374151' : '#f3f4f6', type: 'dashed' } },
      },
      series: [
        {
          name: 'Creadas',
          type: 'line',
          smooth: true,
          showSymbol: true,
          symbolSize: 8,
          data: created,
          lineStyle: { width: 3, color: '#3b82f6' },
          itemStyle: { color: '#3b82f6' },
        },
        {
          name: 'Cerradas (Ganadas + Perdidas)',
          type: 'line',
          smooth: true,
          showSymbol: true,
          symbolSize: 8,
          data: closed,
          lineStyle: { width: 3, color: '#10b981' },
          itemStyle: { color: '#10b981' },
        },
      ],
    };
  });

  ngOnInit(): void {
    this.watchDarkMode();
    this.loadMetrics();
  }

  loadMetrics(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    const params = {
      start_date: this.startDate(),
      end_date: this.endDate(),
    };

    this.metricsService
      .getMetrics(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.metrics.set(res.data);
          this.isLoading.set(false);
        },
        error: () => {
          this.metrics.set(null);
          this.isLoading.set(false);
          this.errorMessage.set('No se han podido cargar las métricas.');
        },
      });
  }

  onDateRangeChange(range: { start: string | null; end: string | null }) {
    this.startDate.set(range.start);
    this.endDate.set(range.end);
    this.loadMetrics();
  }

  formatCurrency(value: number | undefined): string {
    if (value === undefined) return '0 €';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value);
  }

  formatPercent(value: number | undefined): string {
    if (value === undefined) return '0.0%';
    return `${value.toFixed(1)}%`;
  }

  private isDarkMode(): boolean {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  private watchDarkMode(): void {
    this.darkMode.set(this.isDarkMode());
    const observer = new MutationObserver(() => {
      this.darkMode.set(this.isDarkMode());
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  get hasActiveFilters(): boolean {
    return !!this.startDate() || !!this.endDate();
  }

  clearFilters(): void {
    this.startDate.set(null);
    this.endDate.set(null);
    this.loadMetrics();
  }

  get isAdmin(): boolean {
    return this.auth.currentUser?.roles?.includes('admin') || false;
  }
}
